import React from "react";

/**
 * Rudras-AI brand mark — the single source of truth for the platform logo.
 *
 * The asset (PNG, 225×213, RGBA, dark-navy background with a blue gradient
 * "R") lives at `/brand/rudras-logo.png` so the CRA build pipeline ships it
 * unchanged. Because the source PNG already includes its own dark navy
 * canvas, this component just renders it inside a rounded square — that way
 * the same single asset works on light AND dark surfaces without any extra
 * masking.
 *
 * Usage:
 *   <RudrasLogo />                      // default 32×32 (h-8 w-8)
 *   <RudrasLogo size="sm" />            // 24×24 (h-6 w-6) — mobile topbar
 *   <RudrasLogo size="lg" />            // 40×40 (h-10 w-10) — auth screens
 *   <RudrasLogo size="xl" />            // 56×56 (h-14 w-14) — hero / 404
 *   <RudrasLogo className="..." />      // arbitrary overrides
 *
 * The component is intentionally tiny and side-effect-free — no env reads,
 * no fetch, just a deterministic `<img>` with a stable cache-busting
 * version tag so a future asset swap can be invalidated without renaming
 * the file.
 */
export const RUDRAS_LOGO_SRC = "/brand/rudras-logo.png?v=stabilize-feb26";

const SIZE_CLASSES = {
  xs: "h-5 w-5 rounded-[6px]",
  sm: "h-6 w-6 rounded-[7px]",
  md: "h-8 w-8 rounded-[8px]",
  lg: "h-10 w-10 rounded-[10px]",
  xl: "h-14 w-14 rounded-[12px]",
  "2xl": "h-20 w-20 rounded-[16px]",
};

export default function RudrasLogo({
  size = "md",
  className = "",
  alt = "Rudras-AI",
  ringed = false,
  ...rest
}) {
  const sizeCls = SIZE_CLASSES[size] || SIZE_CLASSES.md;
  return (
    <img
      src={RUDRAS_LOGO_SRC}
      alt={alt}
      draggable={false}
      className={[
        sizeCls,
        "object-cover select-none shadow-sm",
        // Subtle ring helps the logo "lift" on busy / glassmorphic surfaces
        // like the cinematic auth panel, but stays off by default so cards
        // and sidebars look flat.
        ringed ? "ring-1 ring-white/30" : "",
        className,
      ].filter(Boolean).join(" ")}
      {...rest}
    />
  );
}
